import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Octopus, SearchDefinition } from '@maximizer/core/shared/domain';
import { map, Observable } from 'rxjs';
import { ContextService } from './context.service';

@Injectable()
export class SearchCatalogService {
  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}

  read(
    entityType: Octopus.EntityType,
    creator?: string | null,
  ): Observable<SearchDefinition[]> {
    const query: Octopus.UntypedQuery[] = [{ EntityType: { $EQ: entityType } }];

    if (creator) {
      query.push({ Creator: { $EQ: { UID: creator } } });
    }

    const request: Octopus.SearchCatalogReadRequest = {
      SearchCatalog: {
        Scope: {
          Fields: {
            Key: 1,
            EntityType: 1,
            Name: 1,
            Description: 1,
            Creator: {
              Key: 1,
              DisplayValue: 1,
            },
            Criteria: 1,
            SecStatus: {
              CanRead: 1,
              CanCreate: 1,
              CanUpdate: 1,
              CanDelete: 1,
            },
            SecAccess: {
              Read: [
                {
                  Key: {
                    Uid: 1,
                    Value: 1,
                  },
                },
              ],
              Write: [
                {
                  Key: {
                    Uid: 1,
                    Value: 1,
                  },
                },
              ],
            },
          },
        },
        Criteria: {
          SearchQuery: query.length === 1 ? query[0] : { $AND: query },
        },
      },
    };

    return this.http
      .post<Octopus.SearchCatalogReadResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        request,
      )
      .pipe(
        map((result) => {
          if (
            result.Code === Octopus.ResponseStatusCode.Successful &&
            result.SearchCatalog
          ) {
            const mapper = new Octopus.SearchCatalogMapper();
            return result.SearchCatalog.Data.map((item) => mapper.from(item));
          }
          return [];
        }),
      );
  }

  create(
    searchDefinition: Partial<SearchDefinition>,
  ): Observable<string | null> {
    const request: Octopus.SearchCatalogWriteRequest = {
      SearchCatalog: {
        Data: {
          Key: null,
          EntityType: searchDefinition.entityType,
          Name: searchDefinition.name,
          Description: searchDefinition.description,
          Criteria: searchDefinition.criteria,
          SecAccess: {
            Read: [searchDefinition.readAccess ?? Octopus.PublicAccessKey],
            Write: [searchDefinition.fullAccess ?? Octopus.PublicAccessKey],
          },
        },
      },
    };

    return this.http
      .post<Octopus.SearchCatalogWriteResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.SearchCatalog.Data.Key;
          }
          return null;
        }),
      );
  }

  update(
    searchDefinition: Partial<SearchDefinition>,
  ): Observable<string | null> {
    if (!searchDefinition.key) {
      throw new Error('SearchCatalog key is required for update');
    }

    const data: Partial<Octopus.SearchCatalog> = {
      Key: searchDefinition.key,
      EntityType: searchDefinition.entityType,
      Name: searchDefinition.name,
      Description: searchDefinition.description,
      Criteria: searchDefinition.criteria,
      SecAccess: {
        Read: [searchDefinition.readAccess ?? Octopus.PublicAccessKey],
        Write: [searchDefinition.fullAccess ?? Octopus.PublicAccessKey],
      },
    };

    const request: Octopus.SearchCatalogWriteRequest = {
      SearchCatalog: {
        Data: data as Octopus.SearchCatalog,
      },
    };

    return this.http
      .post<Octopus.SearchCatalogWriteResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
      )
      .pipe(
        map((result) => {
          if (result?.Code === Octopus.ResponseStatusCode.Successful) {
            return result.SearchCatalog.Data.Key;
          }
          return null;
        }),
      );
  }

  delete(key: string): Observable<boolean> {
    const request: Octopus.SearchCatalogWriteRequest = {
      SearchCatalog: {
        Data: {
          Key: key,
        },
      },
    };

    return this.http
      .post<Octopus.Response>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
      )
      .pipe(
        map((result) => result.Code === Octopus.ResponseStatusCode.Successful),
      );
  }
}
